'use client';
import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import AppLogo from '@/components/ui/AppLogo';
import { Shield, Lock, CheckCircle } from 'lucide-react';

export default function AuthScreen() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 border-r border-border bg-card hero-grid relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-primary/8 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-accent/5 rounded-full blur-3xl" />
        </div>

        <div className="relative">
          <AppLogo
            src="/assets/images/EFF368A5-5FCF-40DA-8BB5-76EDC09B75CA-1779310043613.png"
            size={40}
          />
        </div>

        <div className="relative">
          <p className="text-xs font-semibold tracking-widest uppercase text-accent mb-4">
            Construction Escrow Infrastructure
          </p>
          <h2 className="text-3xl font-bold text-foreground mb-4 text-balance">
            Every fund release requires three independent confirmations
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-8">
            Vaultis enforces a multi-party verification requirement at the protocol level. Developer completion, inspector approval, and investor confirmation must all be present before funds can move.
          </p>

          <div className="space-y-3">
            {[
              { icon: Lock, text: 'Funds locked until all parties confirm' },
              { icon: Shield, text: 'Immutable audit trail of every action' },
              { icon: CheckCircle, text: 'Independent inspection for every milestone' },
            ]?.map((item) => (
              <div key={`auth-trust-${item?.text?.slice(0, 15)}`} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center flex-shrink-0">
                  <item.icon size={14} className="text-accent" />
                </div>
                <span className="text-sm text-muted-foreground">{item?.text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="p-4 rounded-xl border border-border bg-muted/20">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="text-foreground font-medium">Regulated infrastructure.</span> Vaultis operates under escrow compliance standards. All transactions are logged and subject to regulatory review.
            </p>
          </div>
        </div>
      </div>
      {/* Right Panel */}
      <div className="w-full lg:w-1/2 flex flex-col">
        <div className="flex items-center justify-between p-6 lg:hidden border-b border-border">
          <AppLogo
            src="/assets/images/EFF368A5-5FCF-40DA-8BB5-76EDC09B75CA-1779310043613.png"
            size={32}
          />
        </div>

        <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md">
            {/* Tab Switcher */}
            <div className="flex rounded-lg border border-border bg-muted/30 p-1 mb-8">
              <button
                onClick={() => setMode('login')}
                className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all duration-150 ${
                  mode === 'login' ?'bg-card text-foreground shadow-card' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                Sign In
              </button>
              <button
                onClick={() => setMode('signup')}
                className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all duration-150 ${
                  mode === 'signup' ?'bg-card text-foreground shadow-card' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                Create Account
              </button>
            </div>

            {mode === 'login' ? (
              <LoginForm onSwitchToSignup={() => setMode('signup')} />
            ) : (
              <SignupForm onSwitchToLogin={() => setMode('login')} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}